// MARCA | 05/03/2026 | New file – Ghost product custom element for free gift incentive
import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';

// Document-level click handler — completely independent of custom element
// lifecycle. Survives morphing, reconnections, and any timing issues.
var addInProgress = false;
document.addEventListener('click', function (e) {
  var target = /** @type {HTMLElement} */ (e.target);
  var button = /** @type {HTMLButtonElement | null} */ (target.closest('.cart-ghost-product__add-button'));

  if (!button || button.disabled || addInProgress) return;

  var variantId = button.dataset.variantId;
  if (!variantId) return;

  addInProgress = true;

  var textEl = button.querySelector('.cart-ghost-product__button-text');
  var originalText = textEl ? textEl.textContent : 'Add';
  var ghost = /** @type {HTMLElement | null} */ (button.closest('cart-ghost-product'));

  button.disabled = true;
  if (textEl) textEl.textContent = 'Adding...';

  var shopify = /** @type {any} */ (window.Shopify);
  var routes = shopify && shopify.routes ? shopify.routes.root || '/' : '/';

  fetch(routes + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
  })
    .then(function (res) {
      if (!res.ok) throw new Error('Add failed: ' + res.status);
      return res.json();
    })
    .then(function () {
      return fetch(routes + 'cart.js');
    })
    .then(function (res) { return res.json(); })
    .then(function (cartData) {
      document.dispatchEvent(
        new CartAddEvent(cartData, 'ghost-product', {
          itemCount: cartData.item_count,
          source: 'ghost-product',
          variantId: variantId,
        })
      );

      if (textEl) textEl.textContent = 'Added!';
      if (ghost) {
        var g = ghost;
        setTimeout(function () {
          g.classList.add('cart-ghost-product--hidden');
        }, 800);
      }
    })
    .catch(function (err) {
      console.error('[GhostProduct] Add to cart failed:', err);
      if (textEl) textEl.textContent = originalText || 'Add';
      if (button) button.disabled = false;
    })
    .finally(function () {
      addInProgress = false;
    });
});

/**
 * Custom element that manages ghost product display state.
 * Handles locked/unlocked visuals and hides itself when the product is in the cart.
 * The add-to-cart action is handled by the document-level click handler above.
 *
 * @extends {Component}
 */
class CartGhostProductComponent extends Component {
  /** @type {boolean} */
  #isUnlocked = false;

  connectedCallback() {
    super.connectedCallback();
    this.#isUnlocked = this.dataset.unlocked === 'true';

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);

    this.#fetchAndUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
  }

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    var cartData = /** @type {any} */ (event).detail?.resource;

    if (cartData && typeof cartData.total_price !== 'undefined') {
      this.#updateFromCart(cartData);
    } else {
      this.#fetchAndUpdate();
    }
  };

  #fetchAndUpdate = async () => {
    try {
      var response = await fetch('/cart.js');
      var cart = await response.json();
      this.#updateFromCart(cart);
    } catch (error) {
      // Silently fail — cart data will arrive via next event
    }
  };

  /** @param {{ total_price: number, items: Array<{ product_id: number }> }} cart */
  #updateFromCart(cart) {
    if (!cart || typeof cart.total_price === 'undefined' || cart.total_price === null) return;

    var productId = this.dataset.productId || '';
    var alreadyInCart = Array.isArray(cart.items) &&
      cart.items.some(function (item) { return String(item.product_id) === productId; });

    if (alreadyInCart) {
      this.classList.add('cart-ghost-product--hidden');
      return;
    }

    this.classList.remove('cart-ghost-product--hidden');

    var cartCents = cart.total_price;
    if (isNaN(cartCents) || !isFinite(cartCents)) return;

    var thresholdCents = parseFloat(this.dataset.giftThresholdCents || '0');
    var remainingCents = Math.max(0, thresholdCents - cartCents);
    var wasUnlocked = this.#isUnlocked;
    this.#isUnlocked = cartCents >= thresholdCents;

    this.#updateDisplay(remainingCents, wasUnlocked);
  }

  /**
   * @param {number} remainingCents
   * @param {boolean} wasUnlocked
   */
  #updateDisplay(remainingCents, wasUnlocked) {
    var addButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.cart-ghost-product__add-button')
    );
    var unlockMessage = this.querySelector('.cart-ghost-product__unlock-message');

    if (this.#isUnlocked) {
      this.classList.remove('cart-ghost-product--locked');

      if (!wasUnlocked) {
        this.classList.add('cart-ghost-product--unlocking');
        setTimeout(() => {
          this.classList.remove('cart-ghost-product--unlocking');
        }, 600);
      }

      if (addButton) addButton.disabled = false;
    } else {
      this.classList.add('cart-ghost-product--locked');
      if (addButton) addButton.disabled = true;
    }

    if (unlockMessage) {
      if (this.#isUnlocked) {
        unlockMessage.textContent = 'Click to add to cart';
      } else {
        unlockMessage.textContent = 'Spend ' + this.#formatMoney(remainingCents) + ' more to unlock';
      }
    }
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    var shopify = /** @type {any} */ (window.Shopify);
    var win = /** @type {any} */ (window);
    if (typeof shopify !== 'undefined' && shopify.formatMoney) {
      return shopify.formatMoney(cents, win.Theme?.moneyFormat || '£{{amount}}');
    }
    return '£' + (cents / 100).toFixed(2);
  }
}

if (!customElements.get('cart-ghost-product')) {
  customElements.define('cart-ghost-product', CartGhostProductComponent);
}
